/**
 * Deep-link navigation for generic Approval Notifications.
 *
 * Backend sends module / entity_id / route / route_params.
 * Frontend owns the final URL so routes can change without migrations.
 *
 * Recipe / Process approval: always land on the list page (no :id / query deep-link).
 * Demand Batch: always land on Master & Lot (/master-lot), never /batches?tab=6.
 */

export const APPROVAL_NOTIFICATION_EVENT = 'approval_notification';

/** Process approval inbox — always list page, never /processApproval/:id */
const PROCESS_APPROVAL_LIST_ROUTE = '/processApproval';
/** Recipe approval inbox — always land on the list page, never ?recipeId= deep-link */
const RECIPE_APPROVAL_LIST_ROUTE = '/recipe-approval';
/** Production demand / Master & Lot — never legacy /batches?tab=6 */
const MASTER_LOT_ROUTE = '/master-lot';

function isProcessApprovalNotification(item) {
  const moduleKey = (item?.module || item?.entity_type || '').toLowerCase();
  if (moduleKey.includes('process') && !moduleKey.includes('library')) return true;
  const route = item?.route || '';
  return route === PROCESS_APPROVAL_LIST_ROUTE || route.startsWith(`${PROCESS_APPROVAL_LIST_ROUTE}/`);
}

function isRecipeApprovalNotification(item) {
  const moduleKey = (item?.module || item?.entity_type || '').toLowerCase();
  if (
    moduleKey.includes('recipe') ||
    moduleKey.includes('mbmr') ||
    moduleKey === 'bp_recipe_meta_data'
  ) {
    return true;
  }
  const route = (item?.route || '').split('?')[0];
  return route === RECIPE_APPROVAL_LIST_ROUTE || route.startsWith(`${RECIPE_APPROVAL_LIST_ROUTE}/`);
}

function isDemandApprovalNotification(item) {
  const moduleKey = (item?.module || item?.entity_type || '').toLowerCase();
  if (
    moduleKey.includes('demand') ||
    moduleKey.includes('productiondemand') ||
    moduleKey === 'productiondemand'
  ) {
    return true;
  }
  const route = (item?.route || '').split('?')[0];
  if (route === MASTER_LOT_ROUTE || route === '/create-demand') return true;
  // Legacy BE route before Master & Lot moved out of /batches
  if (route === '/batches') {
    const tab = item?.route_params?.tab;
    if (tab === '6' || tab === 6 || item?.route_params?.demandId) return true;
    if (
      String(item?.route || '').includes('tab=6') ||
      String(item?.route || '').includes('demandId=')
    ) {
      return true;
    }
  }
  return false;
}

function demandPath(item) {
  const entityId = item?.entity_id ?? item?.route_params?.demandId ?? null;
  return entityId ? `${MASTER_LOT_ROUTE}?demandId=${entityId}` : MASTER_LOT_ROUTE;
}

/** Build a path (+ optional search) for a notification item. */
export function buildApprovalNotificationPath(item) {
  if (!item) return null;

  if (isProcessApprovalNotification(item)) {
    return PROCESS_APPROVAL_LIST_ROUTE;
  }

  if (isRecipeApprovalNotification(item)) {
    return RECIPE_APPROVAL_LIST_ROUTE;
  }

  if (isDemandApprovalNotification(item)) {
    return demandPath(item);
  }

  // Prefer explicit backend route + params when present.
  if (item.route) {
    const params = item.route_params || {};
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        qs.set(key, String(value));
      }
    });
    const query = qs.toString();
    // If route already has query, merge carefully; else append.
    if (!query) return item.route;
    return item.route.includes('?') ? `${item.route}&${query}` : `${item.route}?${query}`;
  }

  const entityId = item.entity_id;
  const moduleKey = (item.module || item.entity_type || '').toLowerCase();

  if (moduleKey.includes('demand')) {
    return entityId ? `${MASTER_LOT_ROUTE}?demandId=${entityId}` : MASTER_LOT_ROUTE;
  }
  if (moduleKey.includes('process') && !moduleKey.includes('library')) {
    return PROCESS_APPROVAL_LIST_ROUTE;
  }
  if (moduleKey.includes('library')) {
    return entityId
      ? `/process-library-approvals?processId=${entityId}`
      : '/process-library-approvals';
  }
  if (moduleKey.includes('standard')) {
    return entityId ? `/standard-block?id=${entityId}` : '/standard-block';
  }
  if (moduleKey.includes('formula')) {
    return entityId ? `/formula-library?id=${entityId}` : '/formula-library';
  }
  if (moduleKey.includes('recipe') || moduleKey.includes('mbmr') || moduleKey.includes('bmr')) {
    return RECIPE_APPROVAL_LIST_ROUTE;
  }

  return item.route || null;
}

/** Initials for avatar from entity_name / title / module. */
export function approvalNotificationInitials(item) {
  const label = item?.entity_name || item?.entity_code || item?.title || item?.module || '?';
  return String(label)
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 4)
    .toUpperCase();
}

/**
 * Normalize legacy recipe-approval poll items into the generic shape so the
 * Approval tab keeps working until the new backend inbox ships.
 */
export function normalizeLegacyRecipeApprovalItem(item, index = 0) {
  return {
    notification_id: item?.notification_id ?? `legacy-recipe-${index}`,
    title: 'Recipe Approval',
    message: item?.msg || item?.message || 'Recipe approval request',
    module: 'Recipe',
    entity_type: 'bp_recipe_meta_data',
    entity_id: item?.recipe_id ?? item?.entity_id ?? null,
    approval_id: item?.approval_id ?? item?.recipe_approval_id ?? null,
    route: RECIPE_APPROVAL_LIST_ROUTE,
    route_params: {},
    status: item?.status || 'Pending',
    created_at: item?.date || item?.created_at || null,
    is_read: Boolean(item?.is_read),
    entity_name: item?.recipe_name || item?.entity_name || '',
    entity_code: item?.entity_code || '',
    _legacy: true,
  };
}
